import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatMoney, formatDate } from '@/lib/api'
import { usePdfPreview } from '@/components/PdfPreviewModal'
import { useFilial } from '@/lib/filial'
import CanalTag from '@/components/CanalTag'
import { useMostrarCanalTag } from '@/lib/useCanalTag'
import {
  ArrowLeft, FileText, FileSpreadsheet, RefreshCw, BarChart3, Search, X,
  ChevronDown, ChevronRight, ChevronUp, ArrowUpDown, Store, SlidersHorizontal, Check,
  User, CreditCard, Package, Printer, Phone, Receipt, Calendar,
} from 'lucide-react'

// ============================================================================
// Central de Relatórios Gerenciais — MOTOR GENÉRICO TURBINADO (reforma 06/07)
// ----------------------------------------------------------------------------
// v2 (06/07): (1) tabela DENSA (fonte/paddings menores) + colunas essenciais por
//   padrão pra caber na tela sem scroll lateral; (2) CONFIGURADOR de colunas por
//   relatório (o usuário liga/desliga o que quer ver, persistido em localStorage);
//   (3) DRILL redesenhado: em vez de "a mesma tabela num modal", abre um painel de
//   RESUMO com TODAS as métricas da linha em tiles (mostra tudo ao clicar) + a lista
//   de lançamentos individuais logo abaixo, visualmente distinta.
// Contrato back: cada linha traz _filial_id/_filial (seção/seletor) e _key (drill);
//   detalhe = /{endpoint}/detalhe?chave=<_key>&<filtros>&filial_id=<da linha>.
// ============================================================================

const REL: Record<string, any> = {
  'vendas-geral':    { grupo: 'Vendas', titulo: 'Vendas Geral (últimas vendas)', endpoint: 'vendas/geral', filtro: 'periodo', drillLabel: 'Venda', drillTitulo: 'Itens da venda', unidade: 'item(ns)', soFilial: true },
  'vendas-vendedor': { grupo: 'Vendas', titulo: 'Vendas por Vendedor', endpoint: 'vendas/por-vendedor', filtro: 'periodo', drillLabel: 'Vendedor', drillTitulo: 'Vendas do período', unidade: 'venda(s)' },
  'vendas-produto':  { grupo: 'Vendas', titulo: 'Vendas por Produto', endpoint: 'vendas/por-produto', filtro: 'periodo', drillLabel: 'Produto', drillTitulo: 'Vendas do produto', unidade: 'venda(s)' },
  'vendas-cliente':  { grupo: 'Vendas', titulo: 'Vendas por Cliente', endpoint: 'vendas/por-cliente', filtro: 'periodo', drillLabel: 'Cliente', drillTitulo: 'Compras do cliente', unidade: 'pedido(s)' },
  'vendas-periodo':  { grupo: 'Vendas', titulo: 'Vendas por Período', endpoint: 'vendas/por-periodo', filtro: 'periodo', agrupar: true, drillLabel: 'Período', drillTitulo: 'Pedidos do período', unidade: 'pedido(s)' },
  'vendas-grupo':    { grupo: 'Vendas', titulo: 'Vendas por Grupo/Subgrupo', endpoint: 'vendas/por-grupo', filtro: 'periodo', drillLabel: 'Grupo', drillTitulo: 'Produtos do grupo', unidade: 'produto(s)' },
  'vendas-marca':    { grupo: 'Vendas', titulo: 'Vendas por Marca', endpoint: 'vendas/por-marca', filtro: 'periodo', drillLabel: 'Marca', drillTitulo: 'Produtos da marca', unidade: 'produto(s)' },
  // 08/07: bikes usadas recebidas na troca. SEM soFilial de propósito → herda a quebra
  // por filial (seções + seletor + consolidação), atendendo "mostrar de acordo com a filial".
  // Clicar na linha abre o modal da venda (cada linha traz _pedido_id).
  'bikes-usadas':    { grupo: 'Vendas', titulo: 'Bikes Usadas (recebidas na troca)', endpoint: 'vendas/bikes-usadas', filtro: 'periodo', drillLabel: 'Bike', drillTitulo: 'Detalhe da venda', unidade: 'bike(s)' },
  'cp-fornecedor':   { grupo: 'Contas a Pagar', titulo: 'A Pagar por Fornecedor', endpoint: 'contas-pagar/por-fornecedor', filtro: 'venc', drillLabel: 'Fornecedor', drillTitulo: 'Títulos do fornecedor', unidade: 'título(s)' },
  'cp-periodo':      { grupo: 'Contas a Pagar', titulo: 'A Pagar por Período', endpoint: 'contas-pagar/por-periodo', filtro: 'venc', drillLabel: 'Mês', drillTitulo: 'Títulos do mês', unidade: 'título(s)' },
  'cp-status':       { grupo: 'Contas a Pagar', titulo: 'A Pagar por Status', endpoint: 'contas-pagar/por-status', filtro: 'venc', drillLabel: 'Status', drillTitulo: 'Títulos do status', unidade: 'título(s)' },
  'cr-cliente':      { grupo: 'Contas a Receber', titulo: 'A Receber por Cliente', endpoint: 'contas-receber/por-cliente', filtro: 'venc', drillLabel: 'Cliente', drillTitulo: 'Títulos do cliente', unidade: 'título(s)' },
  'cr-periodo':      { grupo: 'Contas a Receber', titulo: 'A Receber por Período', endpoint: 'contas-receber/por-periodo', filtro: 'venc', drillLabel: 'Mês', drillTitulo: 'Títulos do mês', unidade: 'título(s)' },
  'cr-status':       { grupo: 'Contas a Receber', titulo: 'A Receber por Status', endpoint: 'contas-receber/por-status', filtro: 'venc', drillLabel: 'Status', drillTitulo: 'Títulos do status', unidade: 'título(s)' },
  'servicos-tecnico': { grupo: 'Serviços', titulo: 'Serviços por Técnico/Mecânico', endpoint: 'servicos/por-tecnico', filtro: 'periodo', drillLabel: 'Técnico', drillTitulo: 'OS do técnico', unidade: 'OS' },
  'avulsas-natureza': { grupo: 'Fiscal', titulo: 'Notas Avulsas por Natureza', endpoint: 'notas-avulsas/por-natureza', filtro: 'periodo', drillLabel: 'Natureza', drillTitulo: 'Notas da natureza', unidade: 'nota(s)' },
  'oficina-pecas':    { grupo: 'Serviços', titulo: 'Oficina — Peças Vendidas', endpoint: 'servicos/pecas', filtro: 'periodo', drillLabel: 'Peça', drillTitulo: 'OS que usaram a peça', unidade: 'peça(s)' },
  'oficina-pecas-os': { grupo: 'Serviços', titulo: 'Oficina — Peças x Mão de Obra', endpoint: 'servicos/pecas-por-os', filtro: 'periodo', drillLabel: 'OS', drillTitulo: 'Itens da OS', unidade: 'OS' },
}

// Colunas ligadas POR PADRÃO (as essenciais que cabem na tela). O resto fica no
// configurador (engrenagem). A 1ª coluna (dimensão) é sempre fixa/visível.
const DEFAULT_COLS: Record<string, string[]> = {
  'vendas-geral':    ['numero', 'data', 'cliente', 'vendedor', 'bruto', 'desconto', 'liquido', 'custo', 'observacao'],
  'vendas-vendedor': ['vendedor', 'qtd', 'total', 'ticket', 'margem_valor', 'margem_pct', 'ultima_venda'],
  'vendas-produto':  ['codigo', 'descricao', 'ultimo_cliente', 'qtd', 'total', 'margem_pct', 'ultima_venda'],
  'vendas-cliente':  ['cliente', 'qtd', 'total', 'ticket', 'margem_valor', 'ultima_venda', 'dias_sem'],
  'vendas-periodo':  ['data', 'qtd', 'clientes', 'total', 'ticket', 'margem_valor', 'var_pct'],
  'vendas-grupo':    ['grupo', 'subgrupo', 'qtd', 'total', 'margem_pct', 'participacao', 'ultima_venda'],
  'vendas-marca':    ['marca', 'produtos', 'qtd', 'total', 'margem_pct', 'participacao', 'ultima_venda'],
  'bikes-usadas':    ['data', 'numero', 'cliente', 'marca', 'modelo', 'valor', 'situacao', 'codigo'],
  'cp-fornecedor':   ['pessoa', 'qtd', 'total', 'pago', 'aberto', 'vencido_valor', 'atraso_medio'],
  'cp-periodo':      ['mes', 'qtd', 'total', 'pago', 'aberto', 'vencido_valor'],
  'cp-status':       ['status', 'qtd', 'total', 'pago', 'aberto', 'vencido_valor'],
  'cr-cliente':      ['pessoa', 'qtd', 'total', 'pago', 'aberto', 'vencido_valor', 'atraso_medio'],
  'cr-periodo':      ['mes', 'qtd', 'total', 'pago', 'aberto', 'vencido_valor'],
  'cr-status':       ['status', 'qtd', 'total', 'pago', 'aberto', 'vencido_valor'],
  'servicos-tecnico': ['tecnico', 'qtd', 'valor_servicos', 'valor_pecas', 'total', 'ticket', 'garantias', 'ultima_os'],
  'avulsas-natureza': ['natureza', 'qtd', 'total', 'ticket', 'destinatarios', 'ultima'],
  'oficina-pecas':    ['codigo', 'produto', 'qtd', 'preco_medio', 'total', 'os_qtd'],
  'oficina-pecas-os': ['data', 'numero', 'cliente', 'pecas', 'servicos', 'total'],
}

const PRESETS = [
  { v: 'hoje', l: 'Hoje' }, { v: 'mes', l: 'Mês atual' }, { v: 'mes_anterior', l: 'Mês anterior' },
  { v: 'ult30', l: 'Últimos 30 dias' }, { v: 'ult90', l: 'Últimos 90 dias' }, { v: 'personalizado', l: 'Personalizado' },
]

// ---------------------------------------------------------------------------
function fmtCell(v: any, tipo: string) {
  if (v === null || v === undefined || v === '') return tipo === 'texto' ? '' : '—'
  if (tipo === 'moeda') return formatMoney(v)
  if (tipo === 'numero') return Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 })   // qtd/unidades: sem ",00" forçado (1 → "1", 1,5 → "1,5")
  if (tipo === 'inteiro') return Number(v).toLocaleString('pt-BR')
  if (tipo === 'pct') return Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%'
  if (tipo === 'data') return formatDate(v)
  return String(v)
}
const isNum = (t: string) => t === 'moeda' || t === 'numero' || t === 'inteiro' || t === 'pct'
const norm = (s: any) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

// ESC fecha o overlay (modal/popover) antes do ESC-global-volta do Layout.
// Captura + stopPropagation = mesmo padrão de Pessoas.tsx (o Layout detecta o
// overlay via temOverlayAberto e recua). Vale p/ o modal de drill e o menu Colunas.
function useEscClose(onClose: () => void, active = true) {
  useEffect(() => {
    if (!active) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', h, true)
    return () => window.removeEventListener('keydown', h, true)
  }, [onClose, active])
}

function calcTotais(colunas: any[], linhas: any[]) {
  const tot: any = {}
  for (const c of colunas) {
    if (c.key.startsWith('_') || c.naoSomar) continue
    if ((c.tipo === 'moeda' || c.tipo === 'numero' || c.tipo === 'inteiro') && c.key !== 'ticket')
      tot[c.key] = linhas.reduce((s, l) => s + (Number(l[c.key]) || 0), 0)
  }
  if (colunas.some(c => c.key === 'ticket') && tot.qtd) {
    const base = tot.total ?? tot.valor ?? 0
    tot.ticket = tot.qtd ? base / tot.qtd : 0
  }
  if (colunas.some(c => c.key === 'margem_pct') && tot.total) tot.margem_pct = (tot.margem_valor || 0) / tot.total * 100
  if (colunas.some(c => c.key === 'pct_pago') && tot.total) tot.pct_pago = (tot.pago || 0) / tot.total * 100
  return tot
}

function ordenar(linhas: any[], key: string, dir: 'asc' | 'desc', tipo: string) {
  const mult = dir === 'asc' ? 1 : -1
  const num = isNum(tipo)
  return [...linhas].sort((a, b) => {
    const va = a[key], vb = b[key]
    if (num) return (Number(va || 0) - Number(vb || 0)) * mult
    return norm(va).localeCompare(norm(vb)) * mult
  })
}

// ===========================================================================
// Tabela densa reutilizável (relatório + lançamentos do drill) — ordenável
// ===========================================================================
function DataTable({ colunas, linhas, totais, sort, onSort, onRowClick }: {
  colunas: any[]; linhas: any[]; totais?: any; sort?: { key: string; dir: 'asc' | 'desc' };
  onSort?: (k: string, tipo: string) => void; onRowClick?: (l: any) => void;
}) {
  const clickable = !!onRowClick
  const mostrarCanalTag = useMostrarCanalTag()   // tag Loja×Online na visão "Ambos" (09/07)
  const nCols = colunas.length
  // REGRA DE ALINHAMENTO (definida pelo dono, 06/07): primeiro e do MEIO à ESQUERDA;
  // só a ÚLTIMA coluna à DIREITA (ela é a que "sobra" na largura w-full, então à
  // direita encosta na borda sem vão). Ex.: Pedido/Cliente à esquerda, Observação
  // (última) à direita; nos agregados a Última Venda (última) fica à direita.
  const alinhaDir = (ci: number) => ci === nCols - 1
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] leading-tight">
        <thead>
          <tr className="bg-slate-800 text-white">
            {colunas.map((c: any, ci: number) => {
              const dir = alinhaDir(ci)
              return (
                <th key={c.key}
                    onClick={() => onSort?.(c.key, c.tipo)}
                    className={`px-2 py-1.5 font-semibold uppercase tracking-wide whitespace-nowrap select-none ${dir ? 'text-right' : 'text-left'} ${onSort ? 'cursor-pointer hover:bg-slate-700' : ''}`}>
                  <span className="inline-flex items-center gap-0.5">
                    {dir && onSort && <SortIcon active={sort?.key === c.key} dir={sort?.dir} />}
                    {c.label}
                    {!dir && onSort && <SortIcon active={sort?.key === c.key} dir={sort?.dir} />}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l: any, i: number) => (
            <tr key={i} onClick={() => onRowClick?.(l)}
                className={`border-b border-ink-100 ${clickable ? 'cursor-pointer hover:bg-violet-50' : 'hover:bg-slate-50'}`}>
              {colunas.map((c: any, ci: number) => {
                const dir = alinhaDir(ci); const num = isNum(c.tipo)
                const cls = dir
                  ? (num ? 'text-right tabular-nums whitespace-nowrap'
                         : c.tipo === 'data' ? 'text-right whitespace-nowrap'
                                             : 'text-right max-w-[480px] truncate')
                  : (num ? 'text-left tabular-nums whitespace-nowrap'
                         : c.tipo === 'data' ? 'text-left whitespace-nowrap'
                                             : 'text-left max-w-[220px] truncate')
                return (
                  <td key={c.key}
                      title={!num ? String(l[c.key] ?? '') : undefined}
                      className={`px-2 py-1 ${cls} ${ci === 0 ? 'font-medium text-ink-900' : ''}`}>
                    {c.tipo === 'moeda' && Number(l[c.key]) < 0
                      ? <span className="text-rose-600">{fmtCell(l[c.key], c.tipo)}</span>
                      : c.tipo === 'pct'
                        ? <span className={Number(l[c.key]) < 0 ? 'text-rose-600' : ''}>{fmtCell(l[c.key], c.tipo)}</span>
                        : fmtCell(l[c.key], c.tipo)}
                    {/* Tag Loja×Online na 1ª coluna quando a linha é uma venda (traz
                        _caixa_canal, ex.: Vendas Geral) e estamos na visão "Ambos" (09/07). */}
                    {ci === 0 && mostrarCanalTag && l._caixa_canal &&
                      <CanalTag canal={l._caixa_canal} className="ml-1.5 align-middle" />}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        {totais && Object.keys(totais).length > 0 && (
          <tfoot>
            <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
              {colunas.map((c: any, i: number) => (
                <td key={c.key} className={`px-2 py-1.5 ${alinhaDir(i) ? 'text-right' : 'text-left'} ${isNum(c.tipo) ? 'tabular-nums' : ''}`}>
                  {c.key in totais ? fmtCell(totais[c.key], c.tipo) : (i === 0 ? 'TOTAL' : '')}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

function SortIcon({ active, dir }: { active?: boolean; dir?: 'asc' | 'desc' }) {
  if (!active) return <ArrowUpDown size={10} className="opacity-40" />
  return dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />
}

// ===========================================================================
// Configurador de colunas (engrenagem) — liga/desliga o que aparece na tabela
// ===========================================================================
function ColunaPicker({ colunas, visiveis, fixa, onToggle, onReset }: {
  colunas: any[]; visiveis: string[]; fixa: string; onToggle: (k: string) => void; onReset: () => void;
}) {
  const [aberto, setAberto] = useState(false)
  useEscClose(() => setAberto(false), aberto)   // ESC fecha o menu de colunas
  return (
    <div className="relative">
      <button onClick={() => setAberto(a => !a)}
              className="btn btn-sm flex items-center gap-1 border border-ink-200">
        <SlidersHorizontal size={13} /> Colunas
        <span className="text-[10px] text-ink-400">({visiveis.length})</span>
      </button>
      {aberto && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setAberto(false)} />
          <div className="absolute right-0 mt-1 w-60 bg-white border border-ink-200 rounded-lg shadow-xl z-50 p-2">
            <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-ink-100">
              <span className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">Mostrar colunas</span>
              <button onClick={onReset} className="text-[10px] text-violet-600 hover:underline">Padrão</button>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-0.5">
              {colunas.map((c: any) => {
                const on = visiveis.includes(c.key)
                const isFixa = c.key === fixa
                return (
                  <button key={c.key} disabled={isFixa} onClick={() => onToggle(c.key)}
                          className={`w-full flex items-center gap-2 px-1.5 py-1 rounded text-left text-xs ${isFixa ? 'opacity-50 cursor-default' : 'hover:bg-violet-50'}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-violet-600 border-violet-600 text-white' : 'border-ink-300'}`}>
                      {on && <Check size={11} />}
                    </span>
                    <span className="truncate">{c.label}</span>
                    {isFixa && <span className="text-[9px] text-ink-400 ml-auto">fixa</span>}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ===========================================================================
// KPIs de resumo (cartões compactos no topo)
// ===========================================================================
function KpiCards({ colunas, totais }: { colunas: any[]; totais: any }) {
  const cards = colunas.filter(c => c.tipo === 'moeda' && c.key in totais && !c.naoSomar).slice(0, 6)
    .map(c => ({ label: c.label, valor: totais[c.key], tipo: 'moeda' }))
  if (!cards.length) return null
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
      {cards.map((k: any, i: number) => (
        <div key={i} className="card px-3 py-2">
          <div className="text-[10px] text-ink-500 uppercase tracking-wide truncate">{k.label}</div>
          <div className={`text-base font-bold ${Number(k.valor) < 0 ? 'text-rose-600' : 'text-ink-900'}`}>
            {fmtCell(k.valor, k.tipo)}
          </div>
        </div>
      ))}
    </div>
  )
}

// ===========================================================================
// Painel de DRILL — resumo (todas as métricas da linha) + lançamentos
// ===========================================================================
// ===========================================================================
// FEATURE 06/07: MODAL de detalhe de UMA VENDA (pedido) — aberto ao clicar numa
// linha de venda em qualquer relatório (geral ou drill). Fonte: GET /pdv/venda/{id}.
// ===========================================================================
const _FORMA_LBL: Record<string, string> = {
  dinheiro: 'Dinheiro', pix: 'PIX', cartao_debito: 'Cartão débito', cartao_credito: 'Cartão crédito',
  link: 'Link', boleto: 'Boleto', crediario: 'Crediário', prazo: 'A prazo', a_vista: 'À vista',
  credito_cliente: 'Crédito cliente', recebimento_bike: 'Bike (troca)',
}
const formaLabel = (f?: string | null) => (f ? (_FORMA_LBL[f] || f) : '—')

function VendaDetalheModal({ pedidoId, onClose }: { pedidoId: number; onClose: () => void }) {
  useEscClose(onClose)
  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()
  const { data: v, isFetching, error } = useQuery<any>({
    queryKey: ['rel-venda-detalhe', pedidoId],
    queryFn: async () => (await api.get(`/pdv/venda/${pedidoId}`)).data,
  })

  const itens = v?.itens || []
  const subtotal = itens.reduce((a: number, it: any) => a + (Number(it.preco) || 0) * (Number(it.qtd) || 0), 0)
  // pagamentos: array [{forma, valor, parcelas}]; fallback = forma única do pedido.
  const pagamentos: any[] = Array.isArray(v?.pagamentos) && v.pagamentos.length
    ? v.pagamentos
    : (v ? [{ forma: v.forma_pagamento, valor: v.total, parcelas: v.parcelas }] : [])
  // FIX (09/07): o desconto do modal só somava it.desconto (desconto por ITEM) e
  // mostrava R$0 quando o desconto estava no CABEÇALHO (ex.: pedido 223, R$250) ou
  // era a bike da troca. Agora Desconto = desc. itens + desc. cabeçalho + bike, na
  // mesma convenção do relatório Vendas Geral (bike da troca É desconto). Assim
  // Bruto − Desconto = Líquido (o que o cliente paga em dinheiro) fecha.
  const descItens = itens.reduce((a: number, it: any) => a + (Number(it.desconto) || 0), 0)
  const descCabecalho = Number(v?.desconto) || 0
  const bikeTroca = pagamentos.filter((p: any) => p?.forma === 'recebimento_bike')
                              .reduce((a: number, p: any) => a + (Number(p.valor) || 0), 0)
  const descTotal = descItens + descCabecalho + bikeTroca
  const liquido = (Number(v?.total) || 0) - bikeTroca   // total menos a bike (dinheiro recebido)

  const cidadeUf = [v?.cliente_cidade, v?.cliente_uf].filter(Boolean).join('/')

  return (
    <>
    <div className="fixed inset-0 bg-ink-950/75 z-[70] flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4" onClick={e => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className="flex items-start justify-between px-5 py-3 border-b border-ink-200 sticky top-0 bg-white rounded-t-xl z-20">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide flex items-center gap-1"><Receipt size={12} /> Venda</div>
            <h3 className="font-bold text-ink-900 text-lg leading-tight flex items-center gap-2">
              Pedido #{v?.numero ?? '…'}
              {v?.caixa_canal && <CanalTag canal={v.caixa_canal} />}
            </h3>
            <p className="text-xs text-ink-500 flex items-center gap-1"><Calendar size={11} /> {v?.criado_em ? new Date(v.criado_em).toLocaleString('pt-BR') : '—'}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => abrirPdf(`/pdv/venda/${pedidoId}/cupom-pdf?tipo=recibo`, `Cupom #${v?.numero}`)}
                    disabled={!v}
                    className="btn btn-sm flex items-center gap-1 border border-ink-200 disabled:opacity-40">
              <Printer size={13} /> Cupom
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-ink-100 text-ink-500"><X size={18} /></button>
          </div>
        </div>

        {isFetching ? (
          <div className="p-10 text-center text-ink-400 text-sm">Carregando venda…</div>
        ) : error ? (
          <div className="p-10 text-center text-rose-500 text-sm">Não foi possível abrir esta venda (cancelada ou indisponível).</div>
        ) : v ? (
          <>
            {/* Cliente + vendedor */}
            <div className="px-5 py-3 bg-slate-50 border-b border-ink-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-[9px] text-ink-500 uppercase tracking-wide flex items-center gap-1"><User size={11} /> Cliente</div>
                <div className="font-semibold text-ink-900">{v.cliente_nome || 'Consumidor Final'}</div>
                <div className="text-xs text-ink-500">
                  {v.cliente_documento || ''}{v.cliente_telefone ? <span className="inline-flex items-center gap-0.5 ml-1"><Phone size={10} /> {v.cliente_telefone}</span> : ''}
                </div>
                {cidadeUf && <div className="text-[11px] text-ink-400">{cidadeUf}</div>}
              </div>
              <div className="sm:text-right">
                <div className="text-[9px] text-ink-500 uppercase tracking-wide">Vendedor</div>
                <div className="font-medium text-ink-800">{v.vendedor_nome || '—'}</div>
              </div>
            </div>

            {/* Tiles: total, itens, desconto, forma */}
            <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { l: 'Total', val: formatMoney(Number(v.total)), cor: 'text-emerald-700' },
                { l: 'Itens', val: itens.length, cor: 'text-ink-900' },
                { l: 'Desconto', val: formatMoney(descTotal), cor: descTotal > 0 ? 'text-rose-600' : 'text-ink-900' },
                { l: 'Forma', val: pagamentos.length > 1 ? 'Dividido' : formaLabel(pagamentos[0]?.forma), cor: 'text-ink-900' },
              ].map(t => (
                <div key={t.l} className="bg-white rounded-lg border border-ink-100 px-2.5 py-1.5">
                  <div className="text-[9px] text-ink-500 uppercase tracking-wide">{t.l}</div>
                  <div className={`text-sm font-bold tabular-nums ${t.cor}`}>{t.val}</div>
                </div>
              ))}
            </div>

            {/* Pagamentos */}
            <div className="px-5 pb-3">
              <div className="text-[10px] font-semibold text-ink-600 uppercase tracking-wide mb-1 flex items-center gap-1"><CreditCard size={12} /> Pagamento</div>
              <div className="flex flex-wrap gap-1.5">
                {pagamentos.map((pg: any, i: number) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full border border-ink-200 bg-slate-50 px-2.5 py-0.5 text-xs">
                    <span className="font-medium">{formaLabel(pg.forma)}</span>
                    {Number(pg.parcelas) > 1 && <span className="text-ink-400">{pg.parcelas}x</span>}
                    <span className="text-ink-500">{formatMoney(Number(pg.valor) || 0)}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Itens */}
            <div className="px-5 pb-4">
              <div className="text-[10px] font-semibold text-ink-600 uppercase tracking-wide mb-1 flex items-center gap-1"><Package size={12} /> Itens · {itens.length}</div>
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px] leading-tight">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-2 py-1.5 text-left font-semibold uppercase tracking-wide">Produto</th>
                        <th className="px-2 py-1.5 text-right font-semibold uppercase tracking-wide">Qtd</th>
                        <th className="px-2 py-1.5 text-right font-semibold uppercase tracking-wide">Preço</th>
                        <th className="px-2 py-1.5 text-right font-semibold uppercase tracking-wide">Desc.</th>
                        <th className="px-2 py-1.5 text-right font-semibold uppercase tracking-wide">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((it: any, i: number) => (
                        <tr key={i} className="border-b border-ink-100 hover:bg-slate-50">
                          <td className="px-2 py-1 text-ink-900">
                            {it.codigo && <span className="text-ink-400 font-mono mr-1">{it.codigo}</span>}{it.descricao}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums">{Number(it.qtd).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{formatMoney(Number(it.preco))}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-rose-600">{Number(it.desconto) > 0 ? '− ' + formatMoney(Number(it.desconto)) : '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatMoney(Number(it.total))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      {/* Bruto − Desconto(itens+cabeçalho+bike) = Líquido. Rótulo "Líquido"
                          e não "Total" porque a bike da troca abate o dinheiro recebido. */}
                      <tr className="bg-slate-50 font-semibold">
                        <td className="px-2 py-1.5 text-right text-ink-500" colSpan={4}>Bruto / Desconto / Líquido</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatMoney(subtotal)} · <span className="text-rose-600">{formatMoney(descTotal)}</span> · <span className="text-emerald-700">{formatMoney(liquido)}</span></td>
                      </tr>
                      {bikeTroca > 0 && (
                        <tr className="text-[10px] text-ink-500">
                          <td className="px-2 pb-1.5 text-right" colSpan={5}>Inclui bike recebida na troca: <span className="text-rose-600">− {formatMoney(bikeTroca)}</span> · Total da venda {formatMoney(Number(v.total))}</td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </div>
              {v.observacao && <p className="mt-2 text-xs text-ink-500"><span className="font-semibold">Obs:</span> {v.observacao}</p>}
            </div>
          </>
        ) : null}
      </div>
    </div>
    {pdfModal}
    </>
  )
}

function DetalheModal({ cfg, qs, chave, row, resumoColunas, onClose, onAbrirVenda }: {
  cfg: any; qs: string; chave: string; row: any; resumoColunas: any[]; onClose: () => void;
  onAbrirVenda?: (pedidoId: number) => void;
}) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const [baixando, setBaixando] = useState('')
  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()   // PDF no modal (padrão do sistema)
  useEscClose(onClose)   // ESC fecha o painel de detalhe
  const url = `/relatorios-gerenciais/${cfg.endpoint}/detalhe`
  const params = `${qs}${qs ? '&' : ''}chave=${encodeURIComponent(chave)}`

  const { data, isFetching } = useQuery<any>({
    queryKey: ['rel-ger-det', cfg.endpoint, chave, qs],
    queryFn: async () => (await api.get(`${url}?${params}`)).data,
  })

  async function baixar(formato: 'pdf' | 'csv') {
    setBaixando(formato)
    try {
      if (formato === 'pdf') {
        await abrirPdf(`${url}?${params}&formato=pdf`, cfg.drillTitulo)
      } else {
        const r = await api.get(`${url}?${params}&formato=csv`, { responseType: 'blob' })
        const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }))
        const a = document.createElement('a'); a.href = blobUrl; a.download = 'detalhe.csv'; a.click()
        setTimeout(() => URL.revokeObjectURL(blobUrl), 4000)
      }
    } catch { alert('Falha ao gerar o arquivo.') } finally { setBaixando('') }
  }

  const colunas = data?.colunas || []
  const linhasRaw = data?.linhas || []
  const linhas = sort ? ordenar(linhasRaw, sort.key, sort.dir, colunas.find((c: any) => c.key === sort.key)?.tipo || 'texto') : linhasRaw
  const totais = data?.totais || {}

  // tiles de resumo = TODAS as métricas do agregado daquela linha (menos a 1ª = título)
  const tiles = resumoColunas.slice(1).filter(c => row[c.key] !== undefined && row[c.key] !== null)
  const nome = resumoColunas[0] ? String(row[resumoColunas[0].key] ?? '') : ''
  const sub = resumoColunas.slice(1, 3).map(c => c.tipo === 'texto' ? row[c.key] : null).filter(Boolean).join(' · ')

  return (
    <>
    <div className="fixed inset-0 bg-ink-950/70 z-[60] flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-4" onClick={e => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className="flex items-start justify-between px-5 py-3 border-b border-ink-200 sticky top-0 bg-white rounded-t-xl z-20">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide">{cfg.drillLabel}</div>
            <h3 className="font-bold text-ink-900 text-lg leading-tight truncate">{nome}</h3>
            {sub && <p className="text-xs text-ink-500 truncate">{sub}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => baixar('pdf')} disabled={!!baixando || !linhas.length}
                    className="btn btn-sm flex items-center gap-1 border border-ink-200 disabled:opacity-40">
              <FileText size={13} /> {baixando === 'pdf' ? '...' : 'PDF'}
            </button>
            <button onClick={() => baixar('csv')} disabled={!!baixando || !linhas.length}
                    className="btn btn-sm flex items-center gap-1 border border-ink-200 disabled:opacity-40">
              <FileSpreadsheet size={13} /> {baixando === 'csv' ? '...' : 'Excel'}
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-ink-100 text-ink-500"><X size={18} /></button>
          </div>
        </div>

        {/* Resumo — tiles com TODAS as métricas da linha */}
        <div className="px-5 py-3 bg-slate-50 border-b border-ink-100">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {tiles.map((c: any) => (
              <div key={c.key} className="bg-white rounded-lg border border-ink-100 px-2.5 py-1.5">
                <div className="text-[9px] text-ink-500 uppercase tracking-wide truncate">{c.label}</div>
                <div className={`text-sm font-bold tabular-nums ${Number(row[c.key]) < 0 && isNum(c.tipo) ? 'text-rose-600' : 'text-ink-900'}`}>
                  {fmtCell(row[c.key], c.tipo)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lançamentos */}
        <div className="p-4">
          <h4 className="text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">
            {cfg.drillTitulo} {!isFetching && <span className="text-ink-400 font-normal normal-case">· {linhas.length} {cfg.unidade}</span>}
          </h4>
          {isFetching ? (
            <div className="p-8 text-center text-ink-400 text-sm">Carregando…</div>
          ) : !linhas.length ? (
            <div className="p-8 text-center text-ink-400 text-sm">Nenhum lançamento encontrado.</div>
          ) : (
            <>
              <div className="card overflow-hidden">
                {/* 06/07: clicar numa linha de VENDA (com _pedido_id) abre o modal da venda. */}
                <DataTable colunas={colunas} linhas={linhas} totais={totais} sort={sort || undefined}
                           onSort={(k, t) => setSort(s => s?.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: isNum(t) ? 'desc' : 'asc' })}
                           onRowClick={onAbrirVenda ? (l) => { if (l._pedido_id) onAbrirVenda(l._pedido_id) } : undefined} />
              </div>
              {onAbrirVenda && linhas.some((l: any) => l._pedido_id) && (
                <p className="mt-1.5 text-[10px] text-ink-400">Clique numa venda para ver todos os detalhes.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {pdfModal}
    </>
  )
}

// ===========================================================================
// Página principal
// ===========================================================================
function loadColsCfg(): Record<string, string[]> {
  try { return JSON.parse(localStorage.getItem('relger_cols') || '{}') } catch { return {} }
}

export default function RelatorioGerencialView() {
  const { chave } = useParams<{ chave: string }>()
  const cfg = REL[chave || '']

  const [preset, setPreset] = useState('hoje')   // padrão = hoje (usuário troca por tag)
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [agrupar, setAgrupar] = useState<'dia' | 'mes'>('dia')
  const [baixando, setBaixando] = useState('')
  const [busca, setBusca] = useState('')
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const [filialSel, setFilialSel] = useState<string>('')
  const [agruparFilial, setAgruparFilial] = useState(true)
  const [colapsadas, setColapsadas] = useState<Record<string, boolean>>({})
  const [detalhe, setDetalhe] = useState<{ chave: string; row: any; filialId: string } | null>(null)
  const [vendaId, setVendaId] = useState<number | null>(null)   // 06/07: modal de detalhe de venda
  const [colsCfg, setColsCfg] = useState<Record<string, string[]>>(loadColsCfg)
  // FEATURE 06/07: filtros facetados (tags/subtags) — categoria (coluna texto) → valores.
  const [facets, setFacets] = useState<Record<string, string[]>>({})   // colKey → valores selecionados
  const [facetAberta, setFacetAberta] = useState<string | null>(null)  // categoria expandida (subtags)
  // Zera os filtros facetados ao trocar de relatório (o componente não remonta — só o param).
  useEffect(() => { setFacets({}); setFacetAberta(null) }, [chave])
  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()   // PDF no modal (padrão do sistema)
  const { filialAtual } = useFilial()   // p/ relatórios que travam na filial atual (soFilial)

  if (!cfg) return <div className="p-6">Relatório não encontrado. <Link to="/comercial/relatorios-gerenciais" className="text-violet-600">Voltar</Link></div>

  function params(): Record<string, string> {
    const p: Record<string, string> = {}
    if (cfg.filtro === 'periodo') {
      if (preset === 'personalizado') { if (de) p.de = de; if (ate) p.ate = ate }
      else p.periodo = preset
      if (cfg.agrupar) p.agrupar = agrupar
    } else {
      if (de) p.de = de
      if (ate) p.ate = ate
    }
    return p
  }
  const qs = new URLSearchParams(params()).toString()

  // Relatórios com soFilial (ex.: Vendas Geral) NUNCA consolidam: travam na filial
  // ATUAL (filialAtual), enviando filial_id explícito mesmo em modo consolidado.
  const filialLock = cfg.soFilial && filialAtual?.id ? String(filialAtual.id) : ''
  const filialEfetiva = filialLock || filialSel
  const qsMain = filialLock ? `${qs}${qs ? '&' : ''}filial_id=${filialLock}` : qs

  const { data, isFetching, refetch } = useQuery<any>({
    queryKey: ['rel-ger', chave, qsMain],
    queryFn: async () => (await api.get(`/relatorios-gerenciais/${cfg.endpoint}?${qsMain}`)).data,
  })

  async function baixar(formato: 'pdf' | 'csv') {
    setBaixando(formato)
    try {
      const sep = qs ? '&' : ''
      const extra = filialEfetiva ? `${sep}filial_id=${filialEfetiva}` : ''
      // 08/07: envia as COLUNAS ATIVAS do seletor p/ o PDF/CSV sair só com elas (backend filtra).
      const colsParam = visiveis.length ? `&cols=${encodeURIComponent(visiveis.join(','))}` : ''
      const q2 = `${qs}${extra}${colsParam}`; const s2 = q2 ? '&' : ''
      const apiUrl = `/relatorios-gerenciais/${cfg.endpoint}?${q2}${s2}formato=${formato}`
      if (formato === 'pdf') {
        await abrirPdf(apiUrl, cfg.titulo)   // abre no modal, sem nova aba
      } else {
        const r = await api.get(apiUrl, { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }))
        const a = document.createElement('a'); a.href = url; a.download = `${chave}.csv`; a.click()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
      }
    } catch { alert('Falha ao gerar o arquivo.') } finally { setBaixando('') }
  }

  const colunasAll: any[] = data?.colunas || []
  const todasLinhas: any[] = data?.linhas || []
  const firstKey = colunasAll[0]?.key

  // configuração de colunas visíveis (persistida). 1ª coluna sempre presente.
  const allKeys = colunasAll.map(c => c.key)
  const defVis = (DEFAULT_COLS[chave!] || allKeys.slice(0, 8)).filter(k => allKeys.includes(k))
  const visSaved = (colsCfg[chave!] ?? defVis).filter(k => allKeys.includes(k))
  const visiveis = firstKey && !visSaved.includes(firstKey) ? [firstKey, ...visSaved] : visSaved
  const colunas = colunasAll.filter(c => visiveis.includes(c.key))

  function toggleCol(k: string) {
    const cur = colsCfg[chave!] ?? defVis
    const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k]
    const nv = { ...colsCfg, [chave!]: next }
    setColsCfg(nv); localStorage.setItem('relger_cols', JSON.stringify(nv))
  }
  function resetCols() {
    const nv = { ...colsCfg }; delete nv[chave!]
    setColsCfg(nv); localStorage.setItem('relger_cols', JSON.stringify(nv))
  }

  const filiais = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of todasLinhas) if (l._filial_id != null) m.set(String(l._filial_id), l._filial || '—')
    return [...m.entries()].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome))
  }, [todasLinhas])
  const temFilial = filiais.length > 1

  // FEATURE 06/07: colunas facetáveis → tags/subtags. ALLOW-LIST de DIMENSÕES que fazem
  // sentido filtrar (forma/status/vendedor/canal/tipo/grupo/subgrupo/marca) — NÃO colunas de
  // IDENTIDADE (nome do produto, nº do pedido, cliente, observação), que só geram lixo.
  // 08/07: 'situacao' entrou p/ o relatório de Bikes Usadas (filtrar Em estoque/Revendida).
  const FACET_KEYS = new Set(['forma', 'status', 'vendedor', 'canal', 'tipo', 'grupo', 'subgrupo', 'marca', 'situacao'])
  // Facetas de ARRAY (metadados): grupos/subgrupos dos PRODUTOS da venda — filtro por categoria
  // estilo PDV na Vendas Geral. Chave começa com '_' → filtro por PERTENCIMENTO (interseção).
  const ARRAY_FACETS: { key: string; label: string }[] = [
    { key: '_grupos', label: 'Grupo' }, { key: '_subgrupos', label: 'Subgrupo' },
  ]
  const facetCols = useMemo(() => {
    const out: any[] = []
    for (const c of colunasAll.filter((c: any) => c.tipo === 'texto' && FACET_KEYS.has(c.key))) {
      const vals = new Map<string, number>()
      for (const l of todasLinhas) {
        const v = l[c.key]
        if (v == null || v === '' || v === '—') continue
        vals.set(String(v), (vals.get(String(v)) || 0) + 1)
      }
      if (vals.size >= 2 && vals.size <= 40)
        out.push({ key: c.key, label: c.label, isArray: false, valores: [...vals.entries()].sort((a, b) => b[1] - a[1]) })
    }
    for (const af of ARRAY_FACETS) {
      if (!todasLinhas.some((l: any) => Array.isArray(l[af.key]) && l[af.key].length)) continue
      const vals = new Map<string, number>()
      for (const l of todasLinhas) for (const v of (l[af.key] || [])) { if (!v) continue; vals.set(String(v), (vals.get(String(v)) || 0) + 1) }
      if (vals.size >= 2 && vals.size <= 60)
        out.push({ key: af.key, label: af.label, isArray: true, valores: [...vals.entries()].sort((a, b) => b[1] - a[1]) })
    }
    return out
  }, [colunasAll, todasLinhas])

  function toggleFacet(k: string, v: string) {
    setFacets(prev => {
      const cur = new Set(prev[k] || [])
      cur.has(v) ? cur.delete(v) : cur.add(v)
      const arr = [...cur]; const nv = { ...prev }
      if (arr.length) nv[k] = arr; else delete nv[k]
      return nv
    })
  }
  const facetsAtivos = Object.keys(facets).length > 0

  const b = busca.trim()
  let linhas = todasLinhas
  if (filialSel) linhas = linhas.filter(l => String(l._filial_id) === filialSel)
  // Facetas: AND entre categorias, OR entre valores da mesma categoria. Chave com '_' =
  // faceta de ARRAY (grupos/subgrupos da venda) → mantém a linha se INTERSECTA a seleção.
  for (const [k, sel] of Object.entries(facets)) {
    if (!sel || !sel.length) continue
    if (k.startsWith('_')) {
      linhas = linhas.filter((l: any) => Array.isArray(l[k]) && l[k].some((v: any) => sel.includes(String(v))))
    } else {
      linhas = linhas.filter((l: any) => sel.includes(String(l[k])))
    }
  }
  if (b) linhas = linhas.filter((l: any) => colunasAll.some((c: any) => norm(l[c.key]).includes(norm(b))))
  if (sort) linhas = ordenar(linhas, sort.key, sort.dir, colunasAll.find(c => c.key === sort.key)?.tipo || 'texto')

  const filtrado = !!b || !!filialSel || facetsAtivos
  const totais = filtrado ? calcTotais(colunasAll, linhas) : (data?.totais || {})

  function toggleSort(k: string, tipo: string) {
    setSort(s => s?.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: isNum(tipo) ? 'desc' : 'asc' })
  }

  function abrirDetalhe(l: any) {
    // 06/07: se a linha É uma venda (traz _pedido_id, ex.: relatório Vendas Geral),
    // abre direto o MODAL da venda. Senão, faz o drill normal por _key (agregado).
    if (l._pedido_id) { setVendaId(Number(l._pedido_id)); return }
    if (l._key == null) return
    const fid = l._filial_id && l._filial_id > 0 ? String(l._filial_id) : filialEfetiva
    setDetalhe({ chave: String(l._key), row: l, filialId: fid })
  }

  const usarSecoes = agruparFilial && temFilial && !filialSel
  const secoes = useMemo(() => {
    if (!usarSecoes) return []
    const g = new Map<string, any[]>()
    for (const l of linhas) {
      const k = String(l._filial_id)
      if (!g.has(k)) g.set(k, [])
      g.get(k)!.push(l)
    }
    return [...g.entries()]
      .map(([id, ls]) => ({ id, nome: ls[0]._filial || '—', linhas: ls, totais: calcTotais(colunasAll, ls) }))
      .sort((a, b) => (b.totais.total ?? 0) - (a.totais.total ?? 0))
  }, [usarSecoes, linhas, colunasAll])

  return (
    <div className="p-3 lg:p-5 space-y-3">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Link to="/comercial/relatorios-gerenciais" className="text-xs text-ink-500 hover:text-violet-600 flex items-center gap-1 mb-0.5">
            <ArrowLeft size={13} /> Central de Relatórios
          </Link>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <BarChart3 size={19} className="text-violet-600" /> {cfg.titulo}
          </h1>
          {data?.subtitulo && <p className="text-[11px] text-ink-500">{data.subtitulo}</p>}
          {cfg.soFilial && (
            filialAtual
              ? <p className="text-[11px] text-violet-600 font-medium flex items-center gap-1"><Store size={11} /> Filial: {filialAtual.nome_fantasia}</p>
              : <p className="text-[11px] text-amber-600 font-medium">Selecione uma filial para restringir este relatório.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {colunasAll.length > 0 && (
            <ColunaPicker colunas={colunasAll} visiveis={visiveis} fixa={firstKey}
                          onToggle={toggleCol} onReset={resetCols} />
          )}
          <button onClick={() => baixar('pdf')} disabled={!!baixando || !linhas.length}
            className="btn btn-sm flex items-center gap-1 border border-ink-200 disabled:opacity-40">
            <FileText size={14} /> {baixando === 'pdf' ? '...' : 'PDF'}
          </button>
          <button onClick={() => baixar('csv')} disabled={!!baixando || !linhas.length}
            className="btn btn-sm flex items-center gap-1 border border-ink-200 disabled:opacity-40">
            <FileSpreadsheet size={14} /> {baixando === 'csv' ? '...' : 'Excel'}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card p-2.5 flex items-end gap-3 flex-wrap">
        {cfg.filtro === 'periodo' ? (
          <>
            <div>
              <label className="label">Período</label>
              <div className="flex flex-wrap gap-1">
                {PRESETS.map(p => (
                  <button key={p.v} onClick={() => setPreset(p.v)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                            preset === p.v
                              ? 'bg-violet-600 border-violet-600 text-white'
                              : 'bg-white border-ink-200 text-ink-600 hover:border-violet-400 hover:text-violet-700'}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            </div>
            {preset === 'personalizado' && (
              <>
                <div><label className="label">De</label><input type="date" className="input" value={de} onChange={e => setDe(e.target.value)} /></div>
                <div><label className="label">Até</label><input type="date" className="input" value={ate} onChange={e => setAte(e.target.value)} /></div>
              </>
            )}
            {cfg.agrupar && (
              <div>
                <label className="label">Agrupar por</label>
                <select className="input" value={agrupar} onChange={e => setAgrupar(e.target.value as any)}>
                  <option value="dia">Dia</option><option value="mes">Mês</option>
                </select>
              </div>
            )}
          </>
        ) : (
          <>
            <div><label className="label">Vencimento de</label><input type="date" className="input" value={de} onChange={e => setDe(e.target.value)} /></div>
            <div><label className="label">até</label><input type="date" className="input" value={ate} onChange={e => setAte(e.target.value)} /></div>
            <span className="text-[11px] text-ink-400 pb-2">Em branco = todos</span>
          </>
        )}
        {temFilial && (
          <div>
            <label className="label flex items-center gap-1"><Store size={12} /> Filial</label>
            <select className="input" value={filialSel} onChange={e => setFilialSel(e.target.value)}>
              <option value="">Todas as filiais</option>
              {filiais.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
        )}
        <button onClick={() => refetch()} className="btn btn-sm btn-primary flex items-center gap-1">
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Atualizar
        </button>

        {/* FEATURE 06/07: filtros facetados (tags = categoria, subtags = valores). Client-side
            sobre as linhas carregadas. Multi-seleção; AND entre categorias, OR dentro da categoria. */}
        {facetCols.length > 0 && (
          <div className="w-full border-t border-ink-100 pt-2 mt-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-ink-400 flex items-center gap-1"><SlidersHorizontal size={12} /> Filtros:</span>
              {facetCols.map((f: any) => {
                const sel = facets[f.key] || []
                const aberta = facetAberta === f.key
                return (
                  <button key={f.key} onClick={() => setFacetAberta(aberta ? null : f.key)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition inline-flex items-center gap-1 ${
                      sel.length ? 'bg-violet-600 border-violet-600 text-white'
                                 : aberta ? 'bg-violet-50 border-violet-400 text-violet-700'
                                          : 'bg-white border-ink-200 text-ink-600 hover:border-violet-400 hover:text-violet-700'}`}>
                    {f.label}{sel.length > 0 && ` · ${sel.length}`}
                    <ChevronDown size={11} className={aberta ? 'rotate-180 transition' : 'transition'} />
                  </button>
                )
              })}
              {facetsAtivos && (
                <button onClick={() => { setFacets({}); setFacetAberta(null) }}
                  className="text-[11px] text-violet-600 hover:underline ml-1">limpar filtros</button>
              )}
            </div>
            {facetAberta && (() => {
              const f = facetCols.find((x: any) => x.key === facetAberta)
              if (!f) return null
              const sel = facets[facetAberta] || []
              return (
                <div className="mt-2 rounded-lg border border-ink-100 bg-slate-50 p-2">
                  <div className="text-[10px] text-ink-500 uppercase tracking-wide mb-1">{f.label} — escolha um ou mais</div>
                  <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                    {f.valores.map(([v, n]: [string, number]) => {
                      const on = sel.includes(v)
                      return (
                        <button key={v} onClick={() => toggleFacet(facetAberta, v)}
                          className={`px-2 py-0.5 rounded text-[11px] border inline-flex items-center gap-1 ${
                            on ? 'bg-violet-100 border-violet-400 text-violet-800 font-medium'
                               : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-100'}`}>
                          {on && <Check size={10} />}{v} <span className="text-ink-400">{n}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })()}
          </div>
        )}
      </div>

      {/* KPIs */}
      {!isFetching && linhas.length > 0 && <KpiCards colunas={colunasAll} totais={totais} />}

      {/* Busca + controles */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-8 pr-8 w-full !py-1 text-xs" placeholder="Buscar na tabela..."
            value={busca} onChange={e => setBusca(e.target.value)} />
          {busca && (
            <button onClick={() => setBusca('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
              <X size={13} />
            </button>
          )}
        </div>
        {temFilial && !filialSel && (
          <label className="flex items-center gap-1.5 text-xs text-ink-600 cursor-pointer select-none">
            <input type="checkbox" checked={agruparFilial} onChange={e => setAgruparFilial(e.target.checked)} />
            Agrupar por filial
          </label>
        )}
        <span className="text-[11px] text-ink-400 ml-auto">
          {filtrado ? `${linhas.length} de ${todasLinhas.length}` : `${linhas.length}`} linha(s) · clique p/ detalhes
        </span>
      </div>

      {/* Tabela / Seções */}
      {isFetching ? (
        <div className="card p-8 text-center text-ink-400 text-sm">Carregando…</div>
      ) : !linhas.length ? (
        <div className="card p-8 text-center text-ink-400 text-sm">{filtrado ? 'Nenhuma linha para o filtro.' : 'Nenhum dado no período selecionado.'}</div>
      ) : usarSecoes ? (
        <div className="space-y-2.5">
          {secoes.map(s => {
            const col = colapsadas[s.id]
            return (
              <div key={s.id} className="card overflow-hidden">
                <button onClick={() => setColapsadas(c => ({ ...c, [s.id]: !c[s.id] }))}
                        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 border-b border-ink-100">
                  <span className="flex items-center gap-2 font-semibold text-ink-800 text-sm">
                    {col ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                    <Store size={14} className="text-violet-600" /> {s.nome}
                    <span className="text-[10px] font-normal text-ink-400">({s.linhas.length})</span>
                  </span>
                  <span className="text-sm font-bold text-ink-900">{fmtCell(s.totais.total ?? 0, 'moeda')}</span>
                </button>
                {!col && (
                  <DataTable colunas={colunas} linhas={s.linhas} totais={s.totais} sort={sort || undefined}
                             onSort={toggleSort} onRowClick={abrirDetalhe} />
                )}
              </div>
            )
          })}
          <div className="card px-3 py-2 flex items-center justify-between bg-violet-50 border-violet-200">
            <span className="font-bold text-violet-900 text-sm">TOTAL GERAL</span>
            <span className="font-bold text-violet-900 text-sm">{fmtCell(totais.total ?? 0, 'moeda')}</span>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <DataTable colunas={colunas} linhas={linhas} totais={totais} sort={sort || undefined}
                     onSort={toggleSort} onRowClick={abrirDetalhe} />
        </div>
      )}

      {detalhe && (
        <DetalheModal cfg={cfg} qs={`${qs}${detalhe.filialId ? `${qs ? '&' : ''}filial_id=${detalhe.filialId}` : ''}`}
                      chave={detalhe.chave} row={detalhe.row} resumoColunas={colunasAll}
                      onClose={() => setDetalhe(null)} onAbrirVenda={(pid) => setVendaId(pid)} />
      )}
      {vendaId != null && <VendaDetalheModal pedidoId={vendaId} onClose={() => setVendaId(null)} />}
      {pdfModal}
    </div>
  )
}
